import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { isDemoMode, demoAdapter } from './demoSandbox';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage (persisted by zustand)
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      try {
        const { state } = JSON.parse(authStorage);
        if (state.accessToken) {
          config.headers.Authorization = `Bearer ${state.accessToken}`;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    // Demo sandbox: route everything through a local adapter so writes never
    // reach the DB (one shared demo login, isolated per browser).
    if (isDemoMode()) {
      config.adapter = demoAdapter as any;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && originalRequest) {
      // Try to refresh token
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        try {
          const { state } = JSON.parse(authStorage);
          if (state.refreshToken) {
            const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
              refreshToken: state.refreshToken,
            });

            const { accessToken, refreshToken: newRefreshToken } = response.data.data;

            // Update stored tokens (refresh token rotates on each use)
            const updatedState = {
              ...state,
              accessToken,
              ...(newRefreshToken && { refreshToken: newRefreshToken }),
            };
            localStorage.setItem(
              'auth-storage',
              JSON.stringify({ state: updatedState })
            );

            // Retry original request
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, clear auth
          localStorage.removeItem('auth-storage');
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// API service functions
export const authService = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
  }) => api.post('/auth/register', data),

  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),

  me: () => api.get('/auth/me'),
};

export const generatorService = {
  list: (params?: { status?: string; state?: string; page?: number; limit?: number }) =>
    api.get('/generators', { params }),

  get: (id: string) => api.get(`/generators/${id}`),

  // Field-tech "near me" search — closest geocoded generators to a point.
  nearby: (params: { lat?: number; lng?: number; address?: string; radius?: number; limit?: number }) =>
    api.get('/generators/nearby', { params }),

  // Per-generator profile status (counts + actionable lists) for the field-tech hero.
  profileStatus: (id: string) => api.get(`/generators/${id}/profile-status`),

  // GPS -> street address to pre-fill the add-a-site form.
  reverseGeocode: (lat: number, lng: number) =>
    api.get('/generators/reverse-geocode', { params: { lat, lng } }),

  create: (data: any) => api.post('/generators', data),

  update: (id: string, data: any) => api.put(`/generators/${id}`, data),

  delete: (id: string) => api.delete(`/generators/${id}`),

  // EPA public-data manifest reconciliation (#12): summary + exceptions,
  // matched to our manifests by MTN. force=true bypasses the 24h pull cache.
  manifestReconciliation: (id: string, force = false) =>
    api.get(`/generators/${id}/manifest-reconciliation`, { params: force ? { force: 1 } : {} }),

  // STEERS — TCEQ-registered RCRA waste codes for this generator.
  // Drives the wizard's Step 5 warning when profile codes aren't
  // present in the generator's STEERS registration.
  getSteers: (id: string) => api.get(`/generators/${id}/steers`),
  setSteers: (id: string, codes: string[]) =>
    api.put(`/generators/${id}/steers`, { codes }),
  verifySteers: (id: string, codes: string[]) =>
    api.post(`/generators/${id}/steers/verify`, { codes }),
  // Live pull from data.texas.gov (no API key needed). Requires the
  // generator to have an swr_number on file. Returns { codes, source:
  // 'tceq-api', rowsFound, yearsCovered, ... }.
  syncSteers: (id: string, lookbackYears = 5) =>
    api.post(`/generators/${id}/steers/sync`, { lookbackYears }),

  // Generation Sites
  listSites: (generatorId: string, params?: { status?: string; page?: number }) =>
    api.get(`/generators/${generatorId}/sites`, { params }),

  getSite: (generatorId: string, siteId: string) =>
    api.get(`/generators/${generatorId}/sites/${siteId}`),

  createSite: (generatorId: string, data: any) =>
    api.post(`/generators/${generatorId}/sites`, data),

  updateSite: (generatorId: string, siteId: string, data: any) =>
    api.put(`/generators/${generatorId}/sites/${siteId}`, data),

  deleteSite: (generatorId: string, siteId: string) =>
    api.delete(`/generators/${generatorId}/sites/${siteId}`),

  // Site Contacts
  addSiteContact: (generatorId: string, siteId: string, data: any) =>
    api.post(`/generators/${generatorId}/sites/${siteId}/contacts`, data),

  updateSiteContact: (generatorId: string, siteId: string, contactId: string, data: any) =>
    api.put(`/generators/${generatorId}/sites/${siteId}/contacts/${contactId}`, data),

  deleteSiteContact: (generatorId: string, siteId: string, contactId: string) =>
    api.delete(`/generators/${generatorId}/sites/${siteId}/contacts/${contactId}`),
};

export const wasteProfileService = {
  list: (params?: {
    generatorId?: string;
    status?: string;
    isHazardous?: boolean;
    page?: number;
    limit?: number;
  }) => api.get('/waste-profiles', { params }),

  get: (id: string) => api.get(`/waste-profiles/${id}`),

  create: (data: any) => api.post('/waste-profiles', data),

  update: (id: string, data: any, lockToken?: string) =>
    api.put(`/waste-profiles/${id}`, data, lockToken ? { headers: { 'X-Lock-Token': lockToken } } : {}),

  // Lock management for draft editing
  acquireLock: (id: string) => api.post(`/waste-profiles/${id}/lock`),
  releaseLock: (id: string, lockToken: string) =>
    api.delete(`/waste-profiles/${id}/lock`, { headers: { 'X-Lock-Token': lockToken } }),

  delete: (id: string) => api.delete(`/waste-profiles/${id}`),

  classify: (id: string) => api.post(`/waste-profiles/${id}/classify`),

  // Disposal-method routing + waste-alternatives recommendations
  // (analyzeDisposalRouting + analyzeAlternatives, deterministic).
  getRecommendations: (id: string, params?: { tsdfId?: string; disposalMethod?: string }) =>
    api.get(`/waste-profiles/${id}/recommendations`, { params }),

  matchTsdfs: (id: string, options?: any) =>
    api.post(`/waste-profiles/${id}/match-tsdfs`, options),

  // Total landed-cost ranking of acceptance-qualified TSDFs (decision #12).
  getLandedCost: (id: string, params?: { limit?: number; maxDistanceMiles?: number; drums?: number; gallons?: number }) =>
    api.get(`/waste-profiles/${id}/landed-cost`, { params }),

  // Bulk-renew approved profiles (extends expiry; skips ones needing a fresh sample).
  bulkRenew: (profileIds: string[]) => api.post('/waste-profiles/bulk-renew', { profileIds }),

  updateStatus: (id: string, status: string, rejectionReason?: string) =>
    api.patch(`/waste-profiles/${id}/status`, { status, rejectionReason }),

  // Submit a draft for review. Backend auto-approves non-hazardous profiles and
  // routes hazardous ones to the admin review queue.
  submitReview: (id: string) => api.post(`/waste-profiles/${id}/submit-review`),

  markComplete: (id: string) =>
    api.post(`/waste-profiles/${id}/mark-complete`),

  // TSDF Submissions
  getSubmissions: (profileId: string, params?: { status?: string; page?: number }) =>
    api.get(`/waste-profiles/${profileId}/submissions`, { params }),

  submitToTsdf: (profileId: string, tsdfId: string, message?: string, sendImmediately?: boolean) =>
    api.post(`/waste-profiles/${profileId}/submissions/submit`, { tsdfId, message, sendImmediately }),

  // Auto-submit to multiple matched TSDFs (platform handles selection)
  autoSubmit: (profileId: string, options?: { maxFacilities?: number; message?: string }) =>
    api.post(`/waste-profiles/${profileId}/submissions/auto-submit`, options),

  getSubmission: (profileId: string, submissionId: string) =>
    api.get(`/waste-profiles/${profileId}/submissions/${submissionId}`),

  sendSubmission: (profileId: string, submissionId: string) =>
    api.post(`/waste-profiles/${profileId}/submissions/${submissionId}/send`),

  cancelSubmission: (profileId: string, submissionId: string, reason?: string) =>
    api.post(`/waste-profiles/${profileId}/submissions/${submissionId}/cancel`, { reason }),

  updateSubmissionStatus: (profileId: string, submissionId: string, data: {
    status: string;
    notes?: string;
    approvalNumber?: string;
    expirationDate?: string;
    conditions?: string;
    fromAI?: boolean;
  }) => api.put(`/waste-profiles/${profileId}/submissions/${submissionId}/status`, data),

  getSubmissionEmails: (profileId: string, submissionId: string) =>
    api.get(`/waste-profiles/${profileId}/submissions/${submissionId}/emails`),

  markEmailRead: (profileId: string, submissionId: string, emailId: string) =>
    api.post(`/waste-profiles/${profileId}/submissions/${submissionId}/emails/${emailId}/read`),

  // Signature preference
  setSignaturePreference: (id: string, data: {
    signaturePreference: 'allow_reuse' | 'request_signature' | 'tpa_sign' | 'pdf_print';
    signatureReuseAuthorized?: boolean;
    signatureAuthorizationText?: string;
  }) => api.put(`/waste-profiles/${id}/signature-preference`, data),
};

export const organizationService = {
  getTransportRate: () => api.get('/organizations/transport-rate'),
  updateTransportRate: (data: { base_rate: number; per_mile_rate: number; per_stop_rate: number; min_charge: number; currency?: string }) =>
    api.put('/organizations/transport-rate', data),
  // Per-org automation opt-ins (all default off).
  getAutomationSettings: () => api.get('/organizations/automation-settings'),
  updateAutomationSettings: (data: any) => api.put('/organizations/automation-settings', data),
};

// Customers (bill-to) above generators. Plan: Customers -> Generators -> Sites.
export const customerService = {
  list: (params?: { search?: string; active?: boolean; page?: number; limit?: number }) =>
    api.get('/customers', { params }),
  get: (id: string) => api.get(`/customers/${id}`),
  create: (data: any) => api.post('/customers', data),
  update: (id: string, data: any) => api.put(`/customers/${id}`, data),
  assignGenerator: (id: string, generatorId: string) =>
    api.post(`/customers/${id}/assign-generator`, { generatorId }),
};

export const classificationService = {
  classify: (data: any) => api.post('/classification/classify', data),

  batchClassify: (items: any[]) => api.post('/classification/batch', { items }),

  getWasteCodes: (type?: string) =>
    api.get('/classification/waste-codes', { params: { type } }),

  getTexasFormCodes: () => api.get('/classification/texas-form-codes'),
};

export const tsdfService = {
  search: (params?: {
    state?: string;
    wasteCodes?: string;
    treatmentMethod?: string;
    isPartner?: boolean;
    page?: number;
    limit?: number;
  }) => api.get('/tsdfs', { params }),

  getTSDFs: (params?: {
    state?: string;
    isPartner?: boolean;
    page?: number;
    limit?: number;
  }) => api.get('/tsdfs', { params }),

  get: (id: string) => api.get(`/tsdfs/${id}`),

  // Broker / admin only on the backend (requireBrokerOrAdmin guard).
  // Generator accounts get 403 if they try.
  create: (data: Record<string, unknown>) => api.post('/tsdfs', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/tsdfs/${id}`, data),
  remove: (id: string) => api.delete(`/tsdfs/${id}`),
  bulkCreate: (rows: Array<Record<string, unknown>>) =>
    api.post('/tsdfs/bulk', { tsdfs: rows }),

  match: (data: {
    wasteCodes?: string[];
    wasteProfileId?: string;
    generatorId?: string;
    quantity?: {
      amount: number;
      unit: string;
      containerType?: string;
      containerCount?: number;
    };
    preferences?: {
      maxDistance?: number;
      treatmentMethods?: string[];
      partnersOnly?: boolean;
      isRush?: boolean;
      limit?: number;
    };
  }) => api.post('/tsdfs/match', data),

  getQuote: (id: string, data: {
    wasteCodes?: string[];
    wasteProfileId?: string;
    quantity?: { amount: number; unit: string };
    isRush?: boolean;
  }) => api.post(`/tsdfs/${id}/quote`, data),

  acceptQuote: (quoteId: string, data?: {
    requestedPickupDate?: string;
    specialInstructions?: string;
  }) => api.post(`/tsdfs/quotes/${quoteId}/accept`, data),

  getQuotes: (params?: { status?: string; page?: number }) =>
    api.get('/tsdfs/quotes', { params }),

  getTreatmentMethods: (wasteCodes?: string[]) =>
    api.get('/tsdfs/treatment-methods', {
      params: { wasteCodes: wasteCodes?.join(',') },
    }),

  checkAcceptance: (id: string, wasteCodes: string[]) =>
    api.get(`/tsdfs/${id}/check-acceptance`, {
      params: { wasteCodes: wasteCodes.join(',') },
    }),
};

export const manifestService = {
  list: (params?: {
    generatorId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
  }) => api.get('/manifests', { params }),

  get: (id: string) => api.get(`/manifests/${id}`),

  create: (data: any) => api.post('/manifests', data),

  update: (id: string, data: any) => api.put(`/manifests/${id}`, data),

  delete: (id: string) => api.delete(`/manifests/${id}`),

  createFromPickup: (pickupId: string, data?: { transporterId?: string; tsdfId?: string }) =>
    api.post(`/manifests/from-pickup/${pickupId}`, data),

  downloadPdf: (id: string) =>
    api.get(`/manifests/${id}/pdf`, { responseType: 'blob' }),

  sign: (id: string, data: any) => api.patch(`/manifests/${id}/sign`, data),
};

export const transportationService = {
  list: (params?: { generatorId?: string; status?: string; page?: number }) =>
    api.get('/transportation', { params }),

  create: (data: any) => api.post('/transportation', data),

  getQuotes: (id: string) => api.get(`/transportation/${id}/quotes`),

  acceptQuote: (id: string, quoteId: string) =>
    api.post(`/transportation/${id}/accept-quote`, { quoteId }),
};

export const documentService = {
  // Waste Profile PDF
  getWasteProfilePdf: (id: string, template?: string) =>
    api.get(`/documents/waste-profile/${id}`, {
      params: { template },
      responseType: 'blob',
    }),

  // EPA Manifest PDF
  getManifestPdf: (id: string) =>
    api.get(`/documents/manifest/${id}`, {
      responseType: 'blob',
    }),

  // LDR Notification PDF
  getLdrNotificationPdf: (manifestId: string) =>
    api.get(`/documents/ldr/${manifestId}`, {
      responseType: 'blob',
    }),

  // Container Label PDF
  getContainerLabelPdf: (wasteProfileId: string, accumulationDate?: string) =>
    api.get(`/documents/container-label/${wasteProfileId}`, {
      params: { accumulationDate },
      responseType: 'blob',
    }),

  generateContainerLabel: (data: {
    generatorId: string;
    wasteProfileId?: string;
    wasteName?: string;
    accumulationDate?: string;
    epaWasteCodes?: string[];
    physicalState?: string;
    hazards?: string[];
  }) =>
    api.post('/documents/container-label', data, {
      responseType: 'blob',
    }),

  // DOT Label PDF
  getDotLabelPdf: (wasteProfileId: string) =>
    api.get(`/documents/dot-label/${wasteProfileId}`, {
      responseType: 'blob',
    }),

  generateDotLabel: (data: {
    dotProperShippingName: string;
    dotHazardClass: string;
    dotUnNumber: string;
    dotPackingGroup?: string;
    dotLabelsRequired?: string[];
    dotReportableQuantity?: boolean;
    dotMarinePollutant?: boolean;
  }) =>
    api.post('/documents/dot-label', data, {
      responseType: 'blob',
    }),

  // Bill of Lading PDF
  getBillOfLadingPdf: (transportationRequestId: string) =>
    api.get(`/documents/bill-of-lading/${transportationRequestId}`, {
      responseType: 'blob',
    }),

  // Preview (returns JSON data)
  getWasteProfilePreview: (id: string) =>
    api.get(`/documents/preview/waste-profile/${id}`),
};

// Helper function to download PDF blob
export const downloadPdf = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Helper function to open PDF in new tab
export const openPdfInNewTab = (blob: Blob) => {
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
};

export const emanifestService = {
  // List e-manifests
  list: (params?: { status?: string; startDate?: string; endDate?: string; page?: number }) =>
    api.get('/emanifest', { params }),

  // Get e-manifest details
  get: (id: string) => api.get(`/emanifest/${id}`),

  // Submit manifest to EPA
  submit: (manifestId: string, data?: { submissionType?: string; emergencyPhone?: string }) =>
    api.post(`/emanifest/submit/${manifestId}`, data),

  // Validate manifest before submission
  validate: (manifestId: string) => api.post(`/emanifest/validate/${manifestId}`),

  // Get status from EPA
  getStatus: (id: string) => api.get(`/emanifest/${id}/status`),

  // Sync status
  syncStatus: (id: string) => api.get(`/emanifest/${id}/status`),

  // Get signatures
  getSignatures: (id: string) => api.get(`/emanifest/${id}/signatures`),

  // Request generator signature
  requestGeneratorSignature: (id: string, data: { epaId: string; name: string; email: string }) =>
    api.post(`/emanifest/${id}/request-signature/generator`, data),

  // Submit signature
  submitSignature: (id: string, data: {
    signerType: 'Generator' | 'Transporter' | 'Tsdf';
    printedName: string;
    transporterOrder?: number;
    signatureAnswer?: string;
    receiptData?: any;
  }) => api.post(`/emanifest/${id}/sign`, data),

  // Get QuickSign link
  getQuickSignLink: (id: string, siteId: string, siteType: 'Generator' | 'Transporter' | 'Tsdf') =>
    api.post(`/emanifest/${id}/quicksign-link`, { siteId, siteType }),

  // Report discrepancy
  reportDiscrepancy: (id: string, data: {
    type: 'Quantity' | 'Type' | 'Residue' | 'PartialRejection' | 'FullRejection' | 'Other';
    description: string;
    alternateFacility?: { epaId: string; name: string };
    wasteLineNumber?: number;
    originalQuantity?: number;
    receivedQuantity?: number;
    unit?: string;
  }) => api.post(`/emanifest/${id}/discrepancy`, data),

  // Submit correction
  submitCorrection: (id: string, corrections: any, reason: string) =>
    api.post(`/emanifest/${id}/correction`, { corrections, reason }),

  // Download signed PDF from EPA
  downloadSignedPdf: (id: string) =>
    api.get(`/emanifest/${id}/pdf`, { responseType: 'blob' }),

  // Get sync history
  getHistory: (id: string) => api.get(`/emanifest/${id}/history`),

  // Sync all pending manifests
  syncAll: () => api.post('/emanifest/sync'),

  // Site management
  checkSiteEligibility: (epaId: string, siteType: 'Generator' | 'Transporter' | 'Tsdf') =>
    api.post('/emanifest/sites/check-eligibility', { epaId, siteType }),

  registerSite: (data: {
    epaId: string;
    siteType: 'Generator' | 'Transporter' | 'Tsdf';
    contactName: string;
    contactEmail: string;
    contactPhone?: string;
    generatorId?: string;
    transporterId?: string;
    tsdfId?: string;
  }) => api.post('/emanifest/sites/register', data),

  lookupSite: (epaId: string) => api.get(`/emanifest/sites/${epaId}`),

  // Lookup codes
  getWasteCodes: () => api.get('/emanifest/codes/waste'),
  getManagementMethods: () => api.get('/emanifest/codes/management-methods'),
};

export const dispatchService = {
  // Pickup Requests
  createPickupRequest: (data: {
    generatorId: string;
    requestedPickupDate: string;
    wasteLines: Array<{
      wasteDescription: string;
      quantity: number;
      quantityUnit: string;
      containerType: string;
      containerCount: number;
      physicalState: string;
      wasteProfileId?: string;
      epaWasteCodes?: string[];
      stateWasteCodes?: string[];
      isHazardous?: boolean;
      dotProperShippingName?: string;
      dotHazardClass?: string;
      dotUnNaNumber?: string;
      dotPackingGroup?: string;
    }>;
    pickupDateFlexibleUntil?: string;
    pickupWindowStart?: string;
    pickupWindowEnd?: string;
    isRush?: boolean;
    pickupContactName?: string;
    pickupContactPhone?: string;
    pickupContactEmail?: string;
    requiresLiftGate?: boolean;
    requiresInsidePickup?: boolean;
    requiresPalletJack?: boolean;
    requiresAppointment?: boolean;
    hasDock?: boolean;
    accessInstructions?: string;
    specialInstructions?: string;
    destinationTsdfId?: string;
    customerPoNumber?: string;
  }) => api.post('/dispatch/pickup-requests', data),

  listPickupRequests: (params?: {
    status?: string;
    generatorId?: string;
    startDate?: string;
    endDate?: string;
    isRush?: boolean;
  }) => api.get('/dispatch/pickup-requests', { params }),

  getPickupRequest: (id: string) => api.get(`/dispatch/pickup-requests/${id}`),

  // Per-waste-profile pickup/shipment history grouped by container size, with
  // TSDF-received weights (null until recorded).
  getProfilePickupHistory: (profileId: string) =>
    api.get(`/dispatch/waste-profiles/${profileId}/pickup-history`),

  // Record received weights for waste lines on a pickup. Anyone scoped to the
  // pickup's org may enter; overriding an existing weight requires
  // overrideAcknowledged + confirmName (must match the acting user's name).
  recordReceivedWeights: (
    pickupId: string,
    weights: Array<{
      wasteLineId: string;
      receivedWeightLbs: number;
      notes?: string;
      overrideAcknowledged?: boolean;
      confirmName?: string;
    }>
  ) => api.post(`/dispatch/pickup-requests/${pickupId}/received-weights`, { weights }),

  updatePickupRequest: (id: string, data: any) =>
    api.put(`/dispatch/pickup-requests/${id}`, data),

  submitPickupRequest: (id: string) =>
    api.post(`/dispatch/pickup-requests/${id}/submit`),

  cancelPickupRequest: (id: string, reason: string) =>
    api.post(`/dispatch/pickup-requests/${id}/cancel`, { reason }),

  // Transporter Matching
  matchTransporters: (pickupRequestId: string) =>
    api.get(`/dispatch/pickup-requests/${pickupRequestId}/match-transporters`),

  calculateRate: (pickupRequestId: string, transporterId: string) =>
    api.post(`/dispatch/pickup-requests/${pickupRequestId}/calculate-rate`, { transporterId }),

  // Assignments
  assignTransporter: (pickupRequestId: string, data: {
    transporterId: string;
    scheduledDate?: string;
    scheduledTime?: string;
    pricingNotes?: string;
    notes?: string;
  }) => api.post(`/dispatch/pickup-requests/${pickupRequestId}/assign`, data),

  getAssignment: (id: string) => api.get(`/dispatch/assignments/${id}`),

  updateAssignmentStatus: (id: string, status: string, data?: any) =>
    api.patch(`/dispatch/assignments/${id}/status`, { status, ...data }),

  confirmAssignment: (id: string, data: {
    confirmedByName: string;
    scheduledDate: string;
    scheduledTime?: string;
  }) => api.post(`/dispatch/assignments/${id}/confirm`, data),

  dispatchDriver: (id: string, data: {
    driverName: string;
    driverPhone: string;
    truckNumber?: string;
    trailerNumber?: string;
    estimatedArrival?: string;
  }) => api.post(`/dispatch/assignments/${id}/dispatch`, data),

  // Communications
  sendDispatchRequest: (assignmentId: string) =>
    api.post(`/dispatch/assignments/${assignmentId}/send-dispatch`),

  sendPickupReminder: (assignmentId: string) =>
    api.post(`/dispatch/assignments/${assignmentId}/send-reminder`),

  // Status Tracking
  addStatusUpdate: (pickupRequestId: string, data: {
    status: string;
    source?: string;
    latitude?: number;
    longitude?: number;
    locationDescription?: string;
    estimatedArrival?: string;
    notes?: string;
    isException?: boolean;
    exceptionType?: string;
    exceptionDescription?: string;
    notifyGenerator?: boolean;
  }) => api.post(`/dispatch/pickup-requests/${pickupRequestId}/status`, data),

  getStatusHistory: (pickupRequestId: string) =>
    api.get(`/dispatch/pickup-requests/${pickupRequestId}/status-history`),

  // Proof of Pickup/Delivery
  recordProof: (assignmentId: string, data: {
    type: 'pickup' | 'delivery';
    signatoryName: string;
    signedAt: string;
    signatoryTitle?: string;
    signatureData?: string;
    photoUrls?: string[];
    bolNumber?: string;
    manifestNumber?: string;
    containerCountVerified?: number;
    quantityDiscrepancyNotes?: string;
    condition?: 'good' | 'damaged' | 'missing_items';
    conditionNotes?: string;
    latitude?: number;
    longitude?: number;
    notes?: string;
  }) => api.post(`/dispatch/assignments/${assignmentId}/proof`, data),

  // Dashboard
  getDashboard: () => api.get('/dispatch/dashboard'),

  // Recommend DOT-segregated, capacity-bounded loads from ready pickups (read-only).
  recommendLoads: (data?: { maxContainers?: number; statuses?: string[] }) =>
    api.post('/dispatch/recommend-loads', data || {}),

  // Loads — bundling pickups onto trailers
  listLoads: (params?: { status?: string; region?: string }) =>
    api.get('/dispatch/loads', { params }),

  getLoad: (id: string) => api.get(`/dispatch/loads/${id}`),

  getLoadPlan: (id: string) => api.get(`/dispatch/loads/${id}/plan`),
};

export const notificationService = {
  getLoginNotifications: () => api.get('/notifications/login'),

  getRenewalSummary: () => api.get('/notifications/renewals/summary'),

  getExpiringProfiles: (days = 30) =>
    api.get('/notifications/renewals/expiring', { params: { days } }),

  getExpiredProfiles: () => api.get('/notifications/renewals/expired'),

  getProfilesNeedingSamples: () => api.get('/notifications/renewals/needs-sample'),

  dismissNotification: (id: string) => api.post(`/notifications/dismiss/${id}`),

  sendRenewalReminders: () => api.post('/notifications/renewals/send-reminders'),

  getShipmentRecommendations: () => api.get('/notifications/shipment-recommendations'),
};

// Credit Services
export const creditService = {
  // Applications
  createApplication: (data: {
    companyHqStreet1: string;
    companyHqStreet2?: string;
    companyHqCity: string;
    companyHqState: string;
    companyHqZip: string;
    dunsNumber?: string;
    taxId?: string;
    requestedCreditLimit?: number;
    yearsInBusiness?: number;
    annualRevenue?: number;
    bankReferences?: any[];
    tradeReferences?: any[];
    notes?: string;
  }) => api.post('/credit/applications', data),

  listApplications: () => api.get('/credit/applications'),

  getApplication: (id: string) => api.get(`/credit/applications/${id}`),

  updateApplication: (id: string, data: any) => api.put(`/credit/applications/${id}`, data),

  submitApplication: (id: string) => api.post(`/credit/applications/${id}/submit`),

  // Credit Summary & Limits
  getSummary: () => api.get('/credit/summary'),

  getLimits: () => api.get('/credit/limits'),

  getLimitDetails: (id: string) => api.get(`/credit/limits/${id}`),

  getLimitTransactions: (id: string, params?: { limit?: number; offset?: number }) =>
    api.get(`/credit/limits/${id}/transactions`, { params }),

  checkAvailability: (tsdfId: string, amount: number) =>
    api.post('/credit/check-availability', { tsdfId, amount }),

  // Admin endpoints
  adminListApplications: (params?: { status?: string; pending?: boolean; limit?: number; offset?: number }) =>
    api.get('/credit/admin/applications', { params }),

  adminReviewApplication: (id: string, data: {
    decision: 'approve' | 'deny';
    approvedLimit?: number;
    notes?: string;
    denialReason?: string;
  }) => api.post(`/credit/admin/applications/${id}/review`, data),

  adminCreateLimit: (data: any) => api.post('/credit/admin/limits', data),

  adminAdjustLimit: (id: string, data: { newLimit: number; reason: string }) =>
    api.post(`/credit/admin/limits/${id}/adjust`, data),

  adminUpdateLimitStatus: (id: string, data: { status: string; reason: string }) =>
    api.post(`/credit/admin/limits/${id}/status`, data),

  adminGetCriteria: () => api.get('/credit/admin/criteria'),

  adminUpdateCriteria: (id: string, data: any) => api.put(`/credit/admin/criteria/${id}`, data),

  adminCreateCriteria: (data: any) => api.post('/credit/admin/criteria', data),

  adminGetHolds: () => api.get('/credit/admin/holds'),

  adminSetHold: (organizationId: string, data: { onHold: boolean; reason: string }) =>
    api.post(`/credit/admin/holds/${organizationId}`, data),
};

// Agreement Services
export const agreementService = {
  // Service Agreements
  getCurrentAgreement: () => api.get('/agreements/current'),

  getAcceptanceStatus: () => api.get('/agreements/status'),

  acceptAgreement: (data: {
    signerName: string;
    signerTitle?: string;
    signerEmail: string;
    acceptanceMethod?: string;
    signatureData?: any;
  }) => api.post('/agreements/accept', data),

  getAcceptanceHistory: () => api.get('/agreements/history'),

  getCYASections: () => api.get('/agreements/cya-sections'),

  requiresAcceptance: (action?: string) =>
    api.get('/agreements/requires-acceptance', { params: { action } }),

  // Profile CYA
  getProfileCYARequirements: (profileId: string) =>
    api.get(`/agreements/profile/${profileId}/cya`),

  getProfileCYAStatus: (profileId: string) =>
    api.get(`/agreements/profile/${profileId}/cya/status`),

  acknowledgeProfileCYA: (profileId: string, data: {
    acknowledgments: {
      accuracy?: boolean;
      non_conforming?: boolean;
      additional_charges?: boolean;
      generator_liability?: boolean;
      sampling_responsibility?: boolean;
    };
    signerName: string;
    customAcknowledgments?: any;
  }) => api.post(`/agreements/profile/${profileId}/cya/acknowledge`, data),

  validateProfileCYA: (profileId: string, action?: string) =>
    api.get(`/agreements/profile/${profileId}/cya/validate`, { params: { action } }),

  getNonConformingDisclaimer: () => api.get('/agreements/non-conforming-disclaimer'),

  // Admin endpoints
  adminListAgreements: () => api.get('/agreements/admin/list'),

  adminCreateAgreement: (data: any) => api.post('/agreements/admin/create', data),

  adminActivateAgreement: (id: string) => api.post(`/agreements/admin/${id}/activate`),

  adminGetNeedsReacceptance: () => api.get('/agreements/admin/needs-reacceptance'),

  adminAddProfileWarning: (profileId: string, data: {
    type?: string;
    severity?: string;
    message: string;
    requiresAcknowledgment?: boolean;
    acknowledgmentText?: string;
  }) => api.post(`/agreements/admin/profile/${profileId}/warning`, data),

  adminRemoveProfileWarning: (profileId: string, warningId: string) =>
    api.delete(`/agreements/admin/profile/${profileId}/warning/${warningId}`),
};

// Pricing Finalization Services
export const pricingFinalizationService = {
  // Initial estimates
  getInitialEstimate: (profileId: string) =>
    api.get(`/pricing/submissions/${profileId}/estimate`),

  // AI pricing suggestions
  processAIPricing: (submissionId: string) =>
    api.post(`/pricing/submissions/${submissionId}/ai-parse`),

  // Pricing confirmation
  getPricingComparison: (submissionId: string) =>
    api.get(`/pricing/submissions/${submissionId}/comparison`),

  confirmPricing: (submissionId: string, data: {
    finalRate: number;
    finalRateUnit: string;
    minimumCharge?: number;
    overrideReason?: string;
  }) => api.post(`/pricing/submissions/${submissionId}/confirm-pricing`, data),

  // Admin pending confirmations
  adminGetPendingConfirmations: () =>
    api.get('/pricing/admin/pending-confirmations'),

  // Quote prepayment and credit
  prepayQuote: (quoteId: string, paymentMethodId: string) =>
    api.post(`/pricing/quotes/${quoteId}/prepay`, { paymentMethodId }),

  allocateCreditForQuote: (quoteId: string) =>
    api.post(`/pricing/quotes/${quoteId}/allocate-credit`),

  // Payment enforcement
  checkSchedulingEligibility: (pickupRequestId: string) =>
    api.get(`/pricing/pickup/${pickupRequestId}/can-schedule`),

  adminOverridePayment: (pickupRequestId: string, reason: string) =>
    api.post(`/pricing/admin/pickup/${pickupRequestId}/override-payment`, { reason }),
};

export const pricingService = {
  // Pricing Calculations
  calculate: (data: {
    pickupRequestId?: string;
    generatorId?: string;
    transporterId?: string;
    tsdfId?: string;
    wasteLines?: any[];
    isRush?: boolean;
    accessorials?: string[];
  }) => api.post('/pricing/calculate', data),

  getEstimate: (data: any) => api.post('/pricing/estimate', data),

  // Rates
  getTransportationRates: (transporterId?: string) =>
    api.get('/pricing/rates/transportation', { params: { transporterId } }),

  getDisposalRates: (tsdfId?: string, wasteCode?: string) =>
    api.get('/pricing/rates/disposal', { params: { tsdfId, wasteCode } }),

  getAccessorialCharges: () => api.get('/pricing/rates/accessorials'),

  getFuelSurcharge: () => api.get('/pricing/rates/fuel-surcharge'),

  // Quotes
  createQuote: (data: {
    pickupRequestId?: string;
    generatorId?: string;
    transporterId?: string;
    tsdfId?: string;
    wasteLines?: any[];
    isRush?: boolean;
    accessorials?: string[];
    validityDays?: number;
    customerNotes?: string;
  }) => api.post('/pricing/quotes', data),

  listQuotes: (params?: {
    status?: string;
    generatorId?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get('/pricing/quotes', { params }),

  getQuote: (id: string) => api.get(`/pricing/quotes/${id}`),

  recalculateQuote: (id: string) => api.post(`/pricing/quotes/${id}/recalculate`),

  sendQuote: (id: string) => api.post(`/pricing/quotes/${id}/send`),

  acceptQuote: (id: string) => api.post(`/pricing/quotes/${id}/accept`),

  rejectQuote: (id: string, reason: string) =>
    api.post(`/pricing/quotes/${id}/reject`, { reason }),

  convertQuoteToInvoice: (id: string) => api.post(`/pricing/quotes/${id}/convert`),

  // Invoices
  createInvoiceFromPickup: (pickupRequestId: string) =>
    api.post('/pricing/invoices/from-pickup', { pickupRequestId }),

  createInvoice: (data: {
    generatorId?: string;
    lineItems: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
      category?: string;
    }>;
    notes?: string;
    paymentTermsDays?: number;
    dueDate?: string;
  }) => api.post('/pricing/invoices', data),

  listInvoices: (params?: {
    paymentStatus?: string;
    generatorId?: string;
    startDate?: string;
    endDate?: string;
    overdue?: boolean;
  }) => api.get('/pricing/invoices', { params }),

  getInvoice: (id: string) => api.get(`/pricing/invoices/${id}`),

  updateInvoice: (id: string, data: {
    notes?: string;
    dueDate?: string;
    paymentTermsDays?: number;
    // #11A in-place line-item edit: send the FULL desired set (existing rows
    // keep their id); the server diffs + recomputes totals, same invoice #.
    lineItems?: Array<{
      id?: string;
      description: string;
      quantity: number;
      unitPrice: number;
      category?: string;
    }>;
  }) => api.patch(`/pricing/invoices/${id}`, data),

  cancelInvoice: (id: string, reason: string) =>
    api.post(`/pricing/invoices/${id}/cancel`, { reason }),

  addAdjustment: (id: string, data: {
    amount: number;
    description: string;
    type: 'credit' | 'charge';
  }) => api.post(`/pricing/invoices/${id}/adjustment`, data),

  sendInvoice: (id: string) => api.post(`/pricing/invoices/${id}/send`),

  recordPayment: (id: string, data: {
    amount: number;
    paymentMethod: string;
    referenceNumber?: string;
    notes?: string;
  }) => api.post(`/pricing/invoices/${id}/payment`, data),

  getOverdueInvoices: () => api.get('/pricing/overdue'),

  // Payments
  createSetupIntent: () => api.post('/pricing/payments/setup-intent'),

  savePaymentMethod: (paymentMethodId: string, isDefault?: boolean) =>
    api.post('/pricing/payments/methods', { paymentMethodId, isDefault }),

  listPaymentMethods: () => api.get('/pricing/payments/methods'),

  setDefaultPaymentMethod: (id: string) =>
    api.post(`/pricing/payments/methods/${id}/default`),

  removePaymentMethod: (id: string) =>
    api.delete(`/pricing/payments/methods/${id}`),

  createPaymentIntent: (invoiceId: string) =>
    api.post('/pricing/payments/intent', { invoiceId }),

  createCheckoutSession: (invoiceId: string, successUrl: string, cancelUrl: string) =>
    api.post('/pricing/payments/checkout', { invoiceId, successUrl, cancelUrl }),

  processRefund: (transactionId: string, amount?: number, reason?: string) =>
    api.post('/pricing/payments/refund', { transactionId, amount, reason }),

  getPaymentSummary: (startDate?: string, endDate?: string) =>
    api.get('/pricing/payments/summary', { params: { startDate, endDate } }),

  // Reports
  getInvoiceSummary: (startDate?: string, endDate?: string) =>
    api.get('/pricing/reports/invoices', { params: { startDate, endDate } }),

  getAgingReport: () => api.get('/pricing/reports/aging'),

  generateStatement: (generatorId: string, startDate?: string, endDate?: string) =>
    api.get('/pricing/reports/statement', { params: { generatorId, startDate, endDate } }),

  // Platform Fee
  getPlatformFeeConfig: () => api.get('/pricing/platform-fee'),
};

// SDS Document Services (Block 1.2/1.3)
export const sdsService = {
  list: (params?: { page?: number; limit?: number; status?: string; generator_id?: string }) =>
    api.get('/v1/sds', { params }),

  get: (id: string) => api.get(`/v1/sds/${id}`),

  upload: (file: File, additionalData?: { generator_id?: string; product_name?: string }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (additionalData?.generator_id) formData.append('generator_id', additionalData.generator_id);
    if (additionalData?.product_name) formData.append('product_name', additionalData.product_name);
    return api.post('/v1/sds/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  extract: (id: string) => api.post(`/v1/sds/${id}/extract`),

  classify: (id: string) => api.post(`/v1/sds/${id}/classify`),

  // Re-compute the wizard-ready profile seed against the latest V2 rules.
  // The seed isn't persisted; it's a derivable view over the stored PDF +
  // current classification engine.
  getProfileSeed: (id: string) => api.get(`/v1/sds/${id}/profile-seed`),

  // Log a user correction to an auto-extracted field (parser-tuning queue).
  postExtractionImprovement: (id: string, body: {
    field: string;
    extractedValue?: unknown;
    confirmedValue: unknown;
    sourceLine?: string;
    section?: string;
    notes?: string;
  }) => api.post(`/v1/sds/${id}/extraction-improvements`, body),

  getManualForm: (id: string) => api.get(`/v1/sds/${id}/manual`),

  submitManual: (id: string, data: any) => api.post(`/v1/sds/${id}/manual`, data),

  signOff: (id: string, reason?: string) => api.post(`/v1/sds/${id}/signoff`, { reason }),

  serviceStatus: () => api.get('/v1/sds/service-status'),

  delete: (id: string) => api.delete(`/v1/sds/${id}`),

  download: (id: string) => api.get(`/v1/sds/${id}/download`, { responseType: 'blob' }),
};

// Waste-stream services — contracted-bucket entity grouping multiple SDSs
// that share a classification signature. See StreamMatchingService on the
// backend.
export interface StreamSignature {
  formCode?: string | null;
  isHazardous: boolean;
  texasHazardClass?: string | null;
  physicalState: 'liquid' | 'solid' | 'sludge' | 'gas' | 'aerosol';
  rcraCodes: string[];
  pUCodes?: string[];
}

export const streamsService = {
  list: (params?: { generatorId?: string; status?: string; page?: number; limit?: number }) =>
    api.get('/waste-streams', { params }),

  get: (id: string) => api.get(`/waste-streams/${id}`),

  // No-mutation lookup. Used by the upload UX to render the
  // "attach to existing / create new" prompt.
  match: (body: { generatorId: string; signature: StreamSignature }) =>
    api.post('/waste-streams/match', body),

  create: (body: {
    generatorId: string;
    signature: StreamSignature;
    name?: string;
    productCode?: string;
    description?: string;
    shippingDescription?: Record<string, unknown>;
    sdsDocumentId?: string;
    envelope?: {
      phLow?: number | null;
      phHigh?: number | null;
      flashPointMinF?: number | null;
      specificGravityMin?: number | null;
      specificGravityMax?: number | null;
    };
  }) => api.post('/waste-streams', body),

  attachSds: (id: string, body: { sdsDocumentId: string; normalized: Record<string, unknown> }) =>
    api.post(`/waste-streams/${id}/attach-sds`, body),

  update: (id: string, body: {
    name?: string;
    productCode?: string;
    description?: string;
    shippingDescription?: Record<string, unknown>;
  }) => api.patch(`/waste-streams/${id}`, body),

  approve: (id: string) => api.post(`/waste-streams/${id}/approve`),

  archive: (id: string, reason?: string) => api.post(`/waste-streams/${id}/archive`, { reason }),
};

// Broker access request — generator orgs request an upgrade to broker
// status. Admin reviews via /admin/broker-requests/* (separate methods on
// adminService, not exposed here).
export type BrokerRequestStatus = 'pending' | 'approved' | 'denied' | 'withdrawn';
export type ClientCountBucket = '1-5' | '6-20' | '21-50' | '50+';
export type ShipmentBucket = '1-10' | '11-50' | '51-200' | '200+';

export interface BrokerAccessRequest {
  id: string;
  status: BrokerRequestStatus;
  estimatedClientCount: ClientCountBucket;
  estimatedMonthlyShipments: ShipmentBucket;
  yearsBrokering: string | null;
  notes: string | null;
  requestedAt: string;
  reviewedAt: string | null;
  denialReason: string | null;
}

export const brokerAccessService = {
  /** Latest request for the caller's org, or null if none exists yet. */
  getCurrent: () => api.get<{ success: true; data: BrokerAccessRequest | null }>(
    '/organizations/broker-access-request'
  ),

  submit: (body: {
    estimatedClientCount: ClientCountBucket;
    estimatedMonthlyShipments: ShipmentBucket;
    yearsBrokering?: string;
    notes?: string;
  }) => api.post<{ success: true; data: { id: string; status: BrokerRequestStatus; requestedAt: string } }>(
    '/organizations/broker-access-request',
    body
  ),
};

// Training Data Services (Block 1.1)
export const trainingDataService = {
  stats: (orgId?: string) => api.get('/training-data/stats', { params: { orgId } }),

  reviewQueue: (params?: { limit?: number; taskType?: string; priority?: string; assignedTo?: string }) =>
    api.get('/training-data/review-queue', { params }),

  get: (id: string) => api.get(`/training-data/${id}`),

  assign: (id: string) => api.post(`/training-data/${id}/assign`),

  review: (id: string, data: { accepted: boolean; correction?: any; notes?: string }) =>
    api.post(`/training-data/${id}/review`, data),

  skip: (id: string, reason: string) => api.post(`/training-data/${id}/skip`, { reason }),

  export: (taskType: string, params?: { limit?: number; format?: 'openai' | 'axolotl' }) =>
    api.post('/training-data/export', { taskType, ...params }),
};

// TSDF Directory Services (Block 1.4)
export const tsdfDirectoryService = {
  search: (params?: { query?: string; state?: string; waste_codes?: string; page?: number; limit?: number }) =>
    api.get('/tsdf-directory/search', { params }),

  match: (wasteProfile: any, generatorLocation: any) =>
    api.post('/tsdf-directory/match', { wasteProfile, generatorLocation }),

  get: (id: string) => api.get(`/tsdf-directory/${id}`),

  getAcceptedCodes: (id: string) => api.get(`/tsdf-directory/${id}/accepted-codes`),
};

// TSDF Submission Services (Block 5B)
export const tsdfSubmissionService = {
  list: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get('/tsdf-submissions', { params }),

  get: (id: string) => api.get(`/tsdf-submissions/${id}`),

  create: (profileId: string, tsdfId: string, notes?: string) =>
    api.post('/tsdf-submissions', { profileId, tsdfId, notes }),

  // Log a question the TSDF asked (provider relays it manually today; a
  // future TSDF integration will create these automatically — see RFI phasing).
  recordRfi: (submissionId: string, question: string) =>
    api.post(`/tsdf-submissions/${submissionId}/rfi`, { question }),

  respondToRfi: (submissionId: string, rfiId: string, response: string, attachments?: any[]) =>
    api.post(`/tsdf-submissions/${submissionId}/rfi/${rfiId}/respond`, { response, attachments }),

  cancel: (id: string, reason: string) => api.post(`/tsdf-submissions/${id}/cancel`, { reason }),
};

// AI Service Health
export const aiServiceHealth = {
  getStatus: () => api.get('/v1/sds/service-status'),
};

// Onboarding Service (Block 1.8)
export const onboardingService = {
  getProgress: () => api.get('/onboarding/progress'),

  loadSampleData: () => api.post('/onboarding/load-sample-data'),

  saveCompanyInfo: (data: {
    companyName: string;
    epaId?: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    emergencyPhone?: string;
  }) => api.post('/onboarding/company-info', data),

  completePayment: () => api.post('/onboarding/payment-complete'),

  skipPayment: (reason: string) => api.post('/onboarding/skip-payment', { reason }),

  completeSDS: () => api.post('/onboarding/first-sds-complete'),

  skipSDS: () => api.post('/onboarding/skip-sds'),

  trackFirstManifest: (manifestId: string) =>
    api.post('/onboarding/track-first-manifest', { manifestId }),

  reset: () => api.post('/onboarding/reset'),
};

// Quote Builder Service (Pivot Phase 2)
export const quoteBuilderService = {
  list: (params?: {
    status?: string;
    generator_id?: string;
    page?: number;
    limit?: number;
  }) => api.get('/quote-builder', { params }),

  get: (id: string) => api.get(`/quote-builder/${id}`),

  create: (data: {
    generator_id: string;
    line_items: Array<{
      description: string;
      container_count: number;
      container_type?: string;
      waste_profile_id?: string;
      rate_estimate_id?: string;
      disposal_cost?: number;
      transport_cost?: number;
      fuel_surcharge?: number;
      other_cost?: number;
      notes?: string;
    }>;
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
    pickup_address?: string;
    pickup_window_start?: string;
    pickup_window_end?: string;
    special_instructions?: string;
    customer_notes?: string;
    internal_notes?: string;
    markup_percent?: number;
    valid_until?: string;
  }) => api.post('/quote-builder', data),

  calculate: (data: {
    line_items: Array<{
      description: string;
      container_count: number;
      rate_estimate_id?: string;
      disposal_cost?: number;
      transport_cost?: number;
      fuel_surcharge?: number;
      other_cost?: number;
    }>;
    markup_percent?: number;
  }) => api.post('/quote-builder/calculate', data),

  update: (id: string, data: any) => api.patch(`/quote-builder/${id}`, data),

  delete: (id: string) => api.delete(`/quote-builder/${id}`),

  send: (id: string, data: {
    recipient_email: string;
    recipient_name?: string;
    personal_message?: string;
  }) => api.post(`/quote-builder/${id}/send`, data),

  accept: (id: string) => api.post(`/quote-builder/${id}/accept`),

  decline: (id: string, reason?: string) => api.post(`/quote-builder/${id}/decline`, { reason }),

  getCustomerView: (id: string) => api.get(`/quote-builder/${id}/customer-view`),
};

// Rate Estimates Service (Pivot Phase 2)
export const rateEstimatesService = {
  list: (params?: {
    waste_profile_id?: string;
    generator_id?: string;
    tsdf_id?: string;
    transporter_id?: string;
    rate_type?: string;
    is_locked?: boolean;
    include_expired?: boolean;
    page?: number;
    limit?: number;
  }) => api.get('/rate-estimates', { params }),

  get: (id: string) => api.get(`/rate-estimates/${id}`),

  create: (data: {
    rate_type: 'disposal' | 'transport' | 'fuel_surcharge' | 'other';
    rate_amount: number;
    rate_unit: 'per_drum' | 'per_gallon' | 'per_pound' | 'per_ton' | 'flat' | 'per_mile';
    waste_profile_id?: string;
    generator_id?: string;
    tsdf_id?: string;
    transporter_id?: string;
    container_type?: string;
    effective_date?: string;
    expires_date?: string;
    is_locked?: boolean;
    source?: string;
    reference_number?: string;
    notes?: string;
  }) => api.post('/rate-estimates', data),

  update: (id: string, data: Partial<{
    rate_type: string;
    rate_amount: number;
    rate_unit: string;
    waste_profile_id: string;
    generator_id: string;
    tsdf_id: string;
    transporter_id: string;
    container_type: string;
    effective_date: string;
    expires_date: string;
    is_locked: boolean;
    source: string;
    reference_number: string;
    notes: string;
  }>) => api.patch(`/rate-estimates/${id}`, data),

  delete: (id: string) => api.delete(`/rate-estimates/${id}`),

  getForProfile: (profileId: string) => api.get(`/rate-estimates/for-profile/${profileId}`),

  bulkCreate: (rates: any[], source?: string, reference_number?: string) =>
    api.post('/rate-estimates/bulk', { rates, source, reference_number }),

  getStats: () => api.get('/rate-estimates/stats/overview'),
};

// Document Hub Service (Pivot Phase 1)
export const documentHubService = {
  // Batch management
  createBatch: (totalFiles: number) =>
    api.post('/document-hub/batch', { total_files: totalFiles }),

  getBatch: (id: string) => api.get(`/document-hub/batch/${id}`),

  listBatches: (params?: { limit?: number }) =>
    api.get('/document-hub/batches', { params }),

  // Document upload
  upload: (file: File, options?: {
    document_type?: string;
    batch_id?: string;
    generator_id?: string;
    waste_profile_id?: string;
    linked_sds_id?: string;
    lab_report_type?: string;
    lab_name?: string;
    sample_date?: string;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
    }
    return api.post('/document-hub/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  uploadMultiple: (files: File[], options?: {
    document_type?: string;
    generator_id?: string;
  }) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
    }
    return api.post('/document-hub/upload-multiple', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Document listing and search
  list: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
    generator_id?: string;
    sort_by?: string;
    sort_order?: string;
  }) => api.get('/document-hub', { params }),

  search: (query: string, params?: {
    type?: string;
    status?: string;
    generator_id?: string;
    page?: number;
    limit?: number;
  }) => api.get('/document-hub/search', { params: { q: query, ...params } }),

  // Document details
  get: (id: string) => api.get(`/document-hub/${id}`),

  update: (id: string, data: {
    generator_id?: string;
    waste_profile_id?: string;
    linked_sds_id?: string;
    lab_report_type?: string;
    lab_name?: string;
    sample_date?: string;
  }) => api.patch(`/document-hub/${id}`, data),

  delete: (id: string) => api.delete(`/document-hub/${id}`),

  download: (id: string) => api.get(`/document-hub/${id}/download`, { responseType: 'blob' }),

  reprocess: (id: string) => api.post(`/document-hub/${id}/reprocess`),

  // Statistics
  getStats: () => api.get('/document-hub/stats/overview'),
};

// ===========================================================================
// INTERNAL ADMIN-ONLY TOOLS (ported from unboXed)
// ===========================================================================

export const customerRequestsService = {
  listLanes: () => api.get('/admin/customer-requests/lanes'),
  createLane: (data: { name: string; color?: string; sort_order?: number }) =>
    api.post('/admin/customer-requests/lanes', data),
  updateLane: (id: string, data: any) => api.patch(`/admin/customer-requests/lanes/${id}`, data),
  deleteLane: (id: string) => api.delete(`/admin/customer-requests/lanes/${id}`),

  list: (params?: { status?: string; lane?: string; archived?: boolean }) =>
    api.get('/admin/customer-requests', { params }),
  get: (id: string) => api.get(`/admin/customer-requests/${id}`),
  create: (data: any) => api.post('/admin/customer-requests', data),
  update: (id: string, data: any) => api.patch(`/admin/customer-requests/${id}`, data),
  delete: (id: string) => api.delete(`/admin/customer-requests/${id}`),
  addComment: (id: string, body: string, type?: string) =>
    api.post(`/admin/customer-requests/${id}/comments`, { body, type }),
  bulkReorder: (cards: Array<{ id: string; lane?: string; status?: string; sort_order?: number }>) =>
    api.post('/admin/customer-requests/bulk-reorder', { cards }),
};

export const purchaseOrdersService = {
  list: (params?: { status?: string; customer_request_id?: string; vendor?: string }) =>
    api.get('/admin/purchase-orders', { params }),
  get: (id: string) => api.get(`/admin/purchase-orders/${id}`),
  create: (data: any) => api.post('/admin/purchase-orders', data),
  update: (id: string, data: any) => api.patch(`/admin/purchase-orders/${id}`, data),
  delete: (id: string) => api.delete(`/admin/purchase-orders/${id}`),
};

export const signaturesService = {
  list: (params?: { status?: string }) => api.get('/admin/signatures', { params }),
  get: (id: string) => api.get(`/admin/signatures/${id}`),
  create: (data: {
    document_type: string;
    document_name?: string;
    recipient_email: string;
    recipient_name?: string;
    customer_name?: string;
    job_number?: string;
    location?: string;
    total_amount?: number;
    pdf_base64?: string;
    quote_id?: string;
    customer_request_id?: string;
  }) => api.post('/admin/signatures', data),
  cancel: (id: string) => api.post(`/admin/signatures/${id}/cancel`),
  downloadSigned: (id: string) =>
    api.get(`/admin/signatures/${id}/signed-pdf`, { responseType: 'blob' }),
};

// Public signing endpoints — no auth
export const publicSigningService = {
  view: (token: string) => api.get(`/sign/view/${token}`),
  getPdfUrl: (token: string, signed = false) =>
    `${API_BASE_URL}/sign/pdf/${token}${signed ? '?signed=true' : ''}`,
  complete: (token: string, data: {
    signature_image: string;
    printed_name: string;
    title?: string;
    company?: string;
    purchase_order?: string;
    signed_pdf_base64?: string;
  }) => api.post(`/sign/complete/${token}`, data),
};

export const leadsService = {
  list: (params?: {
    state?: string;
    generator_status?: string;
    min_score?: number;
    limit?: number;
    offset?: number;
    search?: string;
    include_dismissed?: boolean;
  }) => api.get('/admin/leads', { params }),
  get: (id: string) => api.get(`/admin/leads/${id}`),
  upsert: (data: any) => api.post('/admin/leads', data),
  bulk: (leads: any[]) => api.post('/admin/leads/bulk', { leads }),
  dismiss: (id: string) => api.post(`/admin/leads/${id}/dismiss`),
  restore: (id: string) => api.post(`/admin/leads/${id}/restore`),
  stats: () => api.get('/admin/leads/stats/overview'),
};

export const wasteIntelligenceService = {
  analyze: (streams: any[], context?: any) =>
    api.post('/admin/intel/analyze', { streams, context }),
  listReports: () => api.get('/admin/intel/reports'),
  getReport: (id: string) => api.get(`/admin/intel/reports/${id}`),
  saveReport: (data: {
    title?: string;
    generator_id?: string;
    waste_streams: any[];
    findings?: any;
    inputs?: any;
  }) => api.post('/admin/intel/reports', data),
  deleteReport: (id: string) => api.delete(`/admin/intel/reports/${id}`),
};

export const vendorsService = {
  facilities: (params?: {
    state?: string;
    category?: string;
    lat?: number;
    lng?: number;
    radius_miles?: number;
    waste_code?: string;
  }) => api.get('/admin/vendors/facilities', { params }),
  analyzeProspect: (data: { lead_id?: string; handler_id?: string }) =>
    api.post('/admin/vendors/prospects/analyze', data),
};

export const manifestPrinterService = {
  listProfiles: () => api.get('/admin/manifest-printer/profiles'),
  createProfile: (data: any) => api.post('/admin/manifest-printer/profiles', data),
  updateProfile: (id: string, data: any) =>
    api.patch(`/admin/manifest-printer/profiles/${id}`, data),
  deleteProfile: (id: string) => api.delete(`/admin/manifest-printer/profiles/${id}`),
};

export const labAnalyzerService = {
  list: () => api.get('/admin/lab-analyzer'),
  get: (id: string) => api.get(`/admin/lab-analyzer/${id}`),
  analyze: (constituents: Array<{ analyte: string; value: number; unit: string }>) =>
    api.post('/admin/lab-analyzer/analyze', { constituents }),
  save: (data: any) => api.post('/admin/lab-analyzer', data),
  delete: (id: string) => api.delete(`/admin/lab-analyzer/${id}`),
};

export const labPacksService = {
  list: (params?: { generator_id?: string; status?: string; page?: number; limit?: number }) =>
    api.get('/v1/lab-packs', { params }),
  get: (id: string) => api.get(`/v1/lab-packs/${id}`),
  create: (data: any) => api.post('/v1/lab-packs', data),
  addItem: (id: string, item: any) => api.post(`/v1/lab-packs/${id}/items`, item),
  removeItem: (id: string, itemId: string) =>
    api.delete(`/v1/lab-packs/${id}/items/${itemId}`),
  generatePlanV1: (id: string) => api.post(`/v1/lab-packs/${id}/generate-plan`),
  generatePlanV2: (id: string) => api.post(`/v1/lab-packs/${id}/generate-plan-v2`),
  validate: (id: string) => api.post(`/v1/lab-packs/${id}/validate`),
};

// SAM.gov Contract Opportunities — admin-only government-contract pipeline.
// Backend pulls opportunities into a local cache nightly and keyword-scores
// each one; these endpoints browse the cache and manage the bid pipeline.
export type SamPursuitStatus = 'new' | 'reviewing' | 'bidding' | 'submitted' | 'won' | 'lost' | 'no_bid';

export interface SamOpportunityQuery {
  search?: string;
  set_aside?: string;      // 'any' | 'none' | codes, e.g. 'SDVOSBC,SDVOSBS'
  naics?: string;          // codes or prefixes, e.g. '562211,5617'
  psc?: string;
  agency?: string;
  state?: string;          // e.g. 'TX,LA'
  notice_type?: string;
  tier?: string;           // e.g. 'strong,possible'
  environmental?: 'true';
  active?: 'true' | 'false';
  open?: 'true';
  has_pursuit?: 'true' | 'false';
  pursuit_status?: string;
  posted_from?: string;    // YYYY-MM-DD
  posted_to?: string;
  deadline_from?: string;
  deadline_to?: string;
  sort?: 'score' | 'posted' | 'deadline';
  limit?: number;
  offset?: number;
}

export const samGovService = {
  /** Filters: see backend SamGovOpportunityQuery.js. Multi-value params are comma-separated. */
  listOpportunities: (params?: SamOpportunityQuery) => api.get('/admin/sam-gov/opportunities', { params }),
  facets: () => api.get('/admin/sam-gov/facets'),
  getOpportunity: (id: string) => api.get(`/admin/sam-gov/opportunities/${id}`),
  fetchDescription: (id: string) => api.post(`/admin/sam-gov/opportunities/${id}/description`),
  generateNarrative: (id: string) => api.post(`/admin/sam-gov/opportunities/${id}/narrative`),
  listAttachments: (id: string) => api.get(`/admin/sam-gov/opportunities/${id}/attachments`),
  downloadAttachment: (id: string, index: number) =>
    api.get(`/admin/sam-gov/opportunities/${id}/attachments/${index}`, { responseType: 'blob' }),

  sync: (windowDays?: number) => api.post('/admin/sam-gov/sync', { windowDays }),
  startBackfill: (fromDate?: string) => api.post('/admin/sam-gov/backfill', { fromDate }),
  cancelBackfill: () => api.delete('/admin/sam-gov/backfill'),
  syncStatus: () => api.get('/admin/sam-gov/sync/status'),
  stats: () => api.get('/admin/sam-gov/stats'),

  listPursuits: (params?: { status?: SamPursuitStatus }) =>
    api.get('/admin/sam-gov/pursuits', { params }),
  createPursuit: (data: { opportunity_id: string; status?: SamPursuitStatus; internal_notes?: string }) =>
    api.post('/admin/sam-gov/pursuits', data),
  updatePursuit: (id: string, data: {
    status?: SamPursuitStatus;
    internal_notes?: string;
    assigned_to?: string | null;
    bid_amount?: number | string | null;
  }) => api.patch(`/admin/sam-gov/pursuits/${id}`, data),
  deletePursuit: (id: string) => api.delete(`/admin/sam-gov/pursuits/${id}`),

  listKeywords: () => api.get('/admin/sam-gov/keywords'),
  createKeyword: (data: { term: string; weight?: number; category?: string }) =>
    api.post('/admin/sam-gov/keywords', data),
  updateKeyword: (id: string, data: { weight?: number; category?: string; is_active?: boolean }) =>
    api.patch(`/admin/sam-gov/keywords/${id}`, data),
  deleteKeyword: (id: string) => api.delete(`/admin/sam-gov/keywords/${id}`),
  rescore: () => api.post('/admin/sam-gov/keywords/rescore'),
};

export default api;
